/**
 * SideBar.tsx
 * ---------------
 * Component used as a side bar menu when using the DC platform.
 */
import { useContext, useState } from "react";
import {
  MdDashboard,
  MdFeaturedPlayList,
  MdLogout,
  MdOutlineAssignmentInd,
  MdOutlineDashboard,
  MdOutlineExpandMore,
  MdOutlineFeaturedPlayList,
  MdOutlinePeopleAlt,
  MdPeopleAlt,
} from "react-icons/md";
import { BACKEND_URL } from "config/getUrl";
import LogoutPopUp from "../pop-up-panels/logout-pop-up";
import SideBarOption from "./SideBarOption";
import { usePlatform } from "components/utils/PlatformContext";
import { AnimatePresence } from "framer-motion";
import DropdownMenuPopUp from "../pop-up-panels/dropdown-menu-pop-up";
import ProfilePanel from "components/main-view/utils/pop-up-panels/profile-pop-up";
import SavedSuccessfulPanel from "../pop-up-panels/saved-successful-pop-up";
import { MainContext } from "components/utils/Contexts";
import { singlify } from "utils/utils";
import ImageSkeleton from "utils/skeletons/ImageSkeleton";

interface SideBarProps {
  selected: string;
  setActiveItem: React.Dispatch<React.SetStateAction<string>>;
  showSuccesfulSchoolEdit: boolean;
  setShowSuccessfulSchoolEdit: React.Dispatch<React.SetStateAction<boolean>>;
  headerRef: any;
}

const SideBar: React.FC<SideBarProps> = ({
  selected,
  setActiveItem,
  showSuccesfulSchoolEdit,
  setShowSuccessfulSchoolEdit,
  headerRef,
}) => {
  // States definition
  const [showPopUpMenu, setShowPopUpMenu] = useState(false);
  const [showLogoutPopUp, setShowLogoutPopUp] = useState(false);
  const [showProfilePopUp, setShowProfilePopUp] = useState(false);

  // Get the platform context
  const { logo, classesTerminology, studentsTerminology, schoolTerminology } =
    usePlatform();

  // Get the values from the context
  const { userRole, userSchool, userFirstName, userLastName } =
    useContext(MainContext);

  /**
   * Action to perform on click school logo
   */
  const handleClickSchoolProfile = () => {
    // Close the pop up menu
    setShowPopUpMenu(false);

    // First of all, check if the userSchool is defined
    if (!(userSchool && userSchool._id)) return;

    // Set the title and description
    headerRef.current.setHeaderTitle(userSchool.name);
    headerRef.current.setHeaderDescription(
      `${singlify(schoolTerminology)} Panel`,
    );

    // Change the tab to customers
    setActiveItem("school-profile");
  };

  /**
   * Function used when clicking logout in the school logo menu
   */
  const handleClickLogout = () => {
    // Close the current menu
    setShowPopUpMenu(false);
    // And show the pop up modal
    setShowLogoutPopUp(true);
  };

  /**
   * Handle profile pressing
   */
  const handlePressProfile = () => {
    // Close the current menu
    setShowPopUpMenu(false);
    // And show the pop up modal
    setShowProfilePopUp(true);
  };

  const customerAdminMenuOptions = [
    {
      icon: <MdOutlineAssignmentInd className="w-full h-full" />,
      text: `${singlify(schoolTerminology)} Profile`,
      onClickFunction: handleClickSchoolProfile,
    },
    {
      icon: <MdLogout className="w-full h-full" />,
      text: "Logout",
      onClickFunction: handleClickLogout,
    },
  ];

  const classAdminMenuOptions = [
    {
      icon: <MdOutlineAssignmentInd className="w-full h-full" />,
      text: "My Profile",
      onClickFunction: handlePressProfile,
    },
    {
      icon: <MdLogout className="w-full h-full" />,
      text: "Logout",
      onClickFunction: handleClickLogout,
    },
  ];

  const studentMenuOptions = [
    {
      icon: <MdOutlineAssignmentInd className="w-full h-full" />,
      text: "My Profile",
      onClickFunction: handlePressProfile,
    },
    {
      icon: <MdLogout className="w-full h-full" />,
      text: "Logout",
      onClickFunction: handleClickLogout,
    },
  ];

  return (
    <div className="w-[103px] self-stretch inline-flex flex-col justify-between items-center gap-4">
      <div className="px-3 py-4 bg-neutral-50 rounded-3xl flex flex-col justify-start items-start gap-8">
        <SideBarOption
          item="dashboard"
          text="Dashboard"
          icon={
            selected === "dashboard" ? (
              <MdDashboard className="w-full h-full fill-neutral-700" />
            ) : (
              <MdOutlineDashboard className="w-full h-full fill-neutral-700" />
            )
          }
          active={selected === "dashboard"}
          setActiveItem={setActiveItem}
        />
        {["master admin", "customer admin", "class admin"].includes(
          userRole,
        ) && (
          <SideBarOption
            item="classes"
            text="Users"
            icon={
              selected === "classes" ? (
                <MdPeopleAlt className="w-full h-full fill-neutral-700" />
              ) : (
                <MdOutlinePeopleAlt className="w-full h-full fill-neutral-700" />
              )
            }
            active={selected === "classes"}
            setActiveItem={setActiveItem}
          />
        )}
        {["master admin", "customer admin", "class admin", "student"].includes(
          userRole,
        ) && (
          <SideBarOption
            item="lessons"
            text="Lessons"
            icon={
              selected === "lessons" ? (
                <MdFeaturedPlayList className="w-full h-full fill-neutral-700" />
              ) : (
                <MdOutlineFeaturedPlayList className="w-full h-full fill-neutral-700" />
              )
            }
            active={selected === "lessons"}
            setActiveItem={setActiveItem}
          />
        )}
      </div>
      {/* Display school logo for customer and class admins */}
      {["customer admin", "class admin"].includes(userRole) && (
        <div className="relative">
          <button
            className="overflow-hidden flex-col justify-end items-center gap-1 flex cursor-pointer"
            // onClick={handleClickSchoolProfile}
            onClick={() => setShowPopUpMenu(true)}
          >
            <div className="p-1 bg-neutral-50 rounded-full justify-start items-center gap-2 inline-flex">
              <div className="w-16 h-16 rounded-full justify-start items-end gap-2 flex overflow-hidden">
                <ImageSkeleton
                  src={`/api/imgs/organisations/logo/${userSchool?._id}`}
                  alt="school-logo"
                  className="w-full h-full select-none relative"
                />
              </div>
            </div>
            <div className="text-center text-neutral-700 text-xs font-normal font-sans leading-[14px]">
              {userSchool ? userSchool.name : "-"}
            </div>
          </button>
          <AnimatePresence>
            {showPopUpMenu && (
              <DropdownMenuPopUp
                containerStyle="absolute w-fit bottom-[78px] left-1 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex cursor-pointer z-30"
                // Only allow customer admins to edit the school profile
                optionsList={
                  userRole === "customer admin"
                    ? customerAdminMenuOptions
                    : classAdminMenuOptions
                }
                setShowPopUp={setShowPopUpMenu}
              />
            )}
          </AnimatePresence>
        </div>
      )}
      {["root admin", "master admin", "student"].includes(userRole) && (
        <>
          <div className="w-full inline-flex items-center justify-center relative">
            <button
              className="h-fit flex-col justify-center items-center gap-2 inline-flex cursor-pointer"
              onClick={() => setShowPopUpMenu(true)}
            >
              <div className="justify-center items-center gap-0.5 inline-flex">
                <div className="px-[9.50px] py-2.5 bg-neutral-50 rounded-full justify-center items-center gap-2 flex hover:bg-neutral-100 ease-in-out duration-300">
                  <div className="text-secondary-600 text-[23px] font-semibold font-sans leading-7 select-none">
                    {(userFirstName[0] || "-").toUpperCase()}
                    {(userLastName[0] || "-").toUpperCase()}
                  </div>
                </div>
                <div className="w-4 h-4 relative">
                  <MdOutlineExpandMore className="w-4 h-4 text-secondary-600" />
                </div>
              </div>
              <div className="justify-center items-center gap-0.5 inline-flex">
                <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide select-none">
                  My Profile
                </div>
                <div className="w-4 h-4 px-[4.28px] opacity-0 justify-center items-center flex" />
              </div>
            </button>
            <AnimatePresence>
              {showPopUpMenu && (
                <DropdownMenuPopUp
                  containerStyle="absolute w-[155px] bottom-[78px] left-1 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex cursor-pointer z-30"
                  optionsList={studentMenuOptions}
                  setShowPopUp={setShowPopUpMenu}
                />
              )}
            </AnimatePresence>
          </div>
        </>
      )}

      {/* Pop up panels */}
      <AnimatePresence>
        {/* Log out panel */}
        {showLogoutPopUp && <LogoutPopUp setShowPopUp={setShowLogoutPopUp} />}
        {/* Successful school edition message */}
        {showSuccesfulSchoolEdit && (
          <SavedSuccessfulPanel
            key="saved-successfully"
            title={`${singlify(schoolTerminology)} Added Successfully`}
            paragraphs={[
              `The ${singlify(
                schoolTerminology,
              ).toLowerCase()} has been successfully updated in the system.`,
              `Feel free to dive in to start customizing the ${singlify(
                schoolTerminology,
              ).toLowerCase()}'s profile, setting up ${classesTerminology.toLowerCase()}, and inviting staff, ${studentsTerminology.toLowerCase()}, and parents to join the platform.`,
            ]}
            createText="Edit again"
            onCreateAnother={handleClickSchoolProfile}
            onClose={() => setShowSuccessfulSchoolEdit(false)}
          />
        )}
        {showProfilePopUp && (
          <ProfilePanel
            key="profile-panel"
            firstName={userFirstName || "-"}
            lastName={userLastName || "-"}
            setShowPopUp={setShowProfilePopUp}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default SideBar;
